'use client';

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useGameContext } from './GameContext';
import { useAuth } from './AuthContext';
import { getOrCreateGuestUserId, getDisplayName } from '@/lib/guestIdentity';
import { useIsTvRoute } from '@/hooks/useIsTvRoute';
import { useRouter, usePathname } from '@/lib/navigation';

/**
 * Kontekst sesji TV (plan/tv-app-i-pilot-telefon.md, faza 2).
 * Jeden socket z GameContext — serwer trzyma socket.tvSessionId, więc parowanie
 * i gra MUSZĄ iść tym samym połączeniem. Kod sesji rotuje — stabilnym kluczem
 * jest session.id (w sessionStorage jako 'tv.sessionId').
 */
const TvContext = createContext(null);

const SS_CODE = 'tv.sessionCode';
const SS_ID = 'tv.sessionId';
// localStorage, nie sessionStorage: aplikacja TV (WebView) traci sessionStorage przy
// każdym zamknięciu — TV po restarcie ma wrócić do tej samej sesji i przypiętej gry.
const SS_RESUME = 'tv.resumeToken';
// Identyfikator instalacji aplikacji Android TV (`?iid=` w adresie startowym aplikacji) —
// serwer liczy po nim demo i licencję „Tryb TV". Bez aplikacji go nie ma → ekran „pobierz aplikację".
const LS_IID = 'tv.installationId';
const registerPayload = () => {
  try {
    return {
      resumeToken: localStorage.getItem(SS_RESUME) || undefined,
      installationId: localStorage.getItem(LS_IID) || undefined,
    };
  } catch { return {}; }
};

export function TvProvider({ children }) {
  const { socket, isConnected } = useGameContext();
  const { user } = useAuth();
  const isTvRoute = useIsTvRoute();
  const router = useRouter();
  const pathname = usePathname();

  const [role, setRole] = useState(null); // 'phone' | 'tv'
  const [session, setSession] = useState(null);
  const [myUserId, setMyUserId] = useState(null);
  const [storedCode, setStoredCode] = useState(null);
  const [pairStatus, setPairStatus] = useState(null); // 'pending'|'paired'|'rejected'|'lost'|'error'
  const [error, setError] = useState(null);
  const [resumeToken, setResumeToken] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [pairToken, setPairToken] = useState(null);
  const [pairRequests, setPairRequests] = useState([]);
  const [boundGame, setBoundGame] = useState(null);
  // Tryb TV: status zakupu z aplikacji ('verifying' | 'canceled' | 'pending' | 'error' |
  // 'unavailable' | 'verify_failed') i blokada widziana przez telefon (TV bez licencji)
  const [purchaseStatus, setPurchaseStatus] = useState(null);
  const [licenseBlockedAt, setLicenseBlockedAt] = useState(null);
  // Preferencje pilota (persist w localStorage). Lazy init — RemoteBar renderuje
  // się wyłącznie client-side (isRemote wymaga aktywnej sesji), więc brak
  // ryzyka hydration mismatch.
  const [remotePrefs, setRemotePrefs] = useState(() => {
    const defaults = { orientation: 'landscape', phoneAudio: false, vibrate: true };
    if (typeof window === 'undefined') return defaults;
    try {
      return {
        orientation: localStorage.getItem('tv.remoteOrientation') || 'landscape',
        phoneAudio: localStorage.getItem('tv.phoneAudio') === '1',
        vibrate: localStorage.getItem('tv.vibrate') !== '0',
      };
    } catch { return defaults; }
  });
  const setRemotePref = useCallback((key, value) => {
    setRemotePrefs((p) => ({ ...p, [key]: value }));
    try {
      if (key === 'orientation') localStorage.setItem('tv.remoteOrientation', value);
      else if (key === 'phoneAudio') localStorage.setItem('tv.phoneAudio', value ? '1' : '0');
      else if (key === 'vibrate') localStorage.setItem('tv.vibrate', value ? '1' : '0');
    } catch {}
  }, []);

  // isOwner jest pochodną sesji — własność może się przenieść (removeController
  // na backendzie promuje najstarszego kontrolera), o czym dowiadujemy się
  // tylko przez tv:session_update.
  const isOwner = !!session && !!myUserId && session.ownerUserId === myUserId;

  const roleRef = useRef(null);
  const pairStatusRef = useRef(null);
  const userRef = useRef(null);
  const usernameDebounce = useRef(null);
  useEffect(() => { roleRef.current = role; }, [role]);
  useEffect(() => { pairStatusRef.current = pairStatus; }, [pairStatus]);
  useEffect(() => { userRef.current = user; }, [user]);

  // Inicjalizacja po stronie klienta (SSR nie zna sessionStorage/localStorage)
  useEffect(() => {
    queueMicrotask(() => {
      setMyUserId(getOrCreateGuestUserId(user));
      setStoredCode(sessionStorage.getItem(SS_CODE));
    });
  }, [user]);

  const getIds = useCallback(() => {
    const userId = getOrCreateGuestUserId(user);
    return { userId, username: getDisplayName(user) };
  }, [user]);

  const pairedUserIdRef = useRef(null); // userId, z którym telefon jest sparowany
  const emitPair = useCallback((code, token) => {
    if (!socket?.connected) return;
    const { userId, username } = getIds();
    // Token logowania: serwer sam ustala konto (licencja „Tryb TV" na koncie właściciela stołu)
    let authToken;
    try { authToken = localStorage.getItem('token') || undefined; } catch { /* brak storage */ }
    // Po zalogowaniu userId się zmienia (gość → konto): stare id pozwala serwerowi przenieść kontroler
    const prevUserId = pairedUserIdRef.current && pairedUserIdRef.current !== userId ? pairedUserIdRef.current : undefined;
    socket.emit('tv:pair', { code, userId, username, authToken, prevUserId, ...(token ? { pairToken: token } : {}) });
  }, [socket, getIds]);

  // ── Telefon: auto-re-pair po (re)connecie, gdy mamy zapamiętaną sesję ──
  useEffect(() => {
    if (!socket || !isConnected || isTvRoute) return;
    const tryResume = () => {
      const code = sessionStorage.getItem(SS_CODE);
      if (code) emitPair(code);
    };
    tryResume();
    socket.on('connect', tryResume);
    return () => socket.off('connect', tryResume);
  }, [socket, isConnected, isTvRoute, emitPair]);

  // ── Telefon: zmiana nicku albo zalogowanie / wylogowanie po sparowaniu → re-emit tv:pair
  // (debounce 800 ms) — serwer aktualizuje nazwę i konto kontrolera (licencja na koncie) ──
  const lastPairedUsername = useRef(null);
  useEffect(() => {
    if (pairStatus !== 'paired' || isTvRoute) return;
    const username = `${getDisplayName(user)}|${user?._id || ''}`;
    if (username === lastPairedUsername.current) return;
    clearTimeout(usernameDebounce.current);
    usernameDebounce.current = setTimeout(() => {
      const code = session?.code || sessionStorage.getItem(SS_CODE);
      if (code) {
        lastPairedUsername.current = username;
        emitPair(code);
      }
    }, 800);
    return () => clearTimeout(usernameDebounce.current);
  }, [user, pairStatus, session, isTvRoute, emitPair]);

  // ── Wspólny nasłuch zdarzeń tv:* ──
  useEffect(() => {
    if (!socket) return;

    const onRegistered = ({ session: s, resumeToken: rt, qrUrl: q, pairToken: pt }) => {
      localStorage.setItem(SS_RESUME, rt);
      setRole('tv');
      setSession(s);
      setResumeToken(rt);
      setQrUrl(q);
      setPairToken(pt);
      setBoundGame(s.gameCode ? { gameCode: s.gameCode, status: 'waiting' } : null);
      setPairRequests(s.pending || []);
      setError(null);
    };
    const onPaired = ({ code, session: s }) => {
      sessionStorage.setItem(SS_CODE, s?.code || code);
      setStoredCode(s?.code || code);
      if (s?.id) sessionStorage.setItem(SS_ID, s.id);
      setRole('phone');
      setSession(s || null);
      setPairStatus('paired');
      setError(null);
      try { pairedUserIdRef.current = getOrCreateGuestUserId(userRef.current); } catch { /* SSR */ }
      lastPairedUsername.current = `${getDisplayName(userRef.current)}|${userRef.current?._id || ''}`;
    };
    const onPending = () => setPairStatus('pending');
    const onRejected = () => {
      sessionStorage.removeItem(SS_CODE);
      sessionStorage.removeItem(SS_ID);
      setStoredCode(null);
      setPairStatus('rejected');
    };
    const onUnpaired = () => {
      sessionStorage.removeItem(SS_CODE);
      sessionStorage.removeItem(SS_ID);
      setStoredCode(null);
      setSession(null);
      setPairStatus(null);
      setBoundGame(null);
    };
    const onSessionUpdate = ({ session: s }) => {
      setSession(s);
      if (roleRef.current === 'phone' && s?.code) {
        sessionStorage.setItem(SS_CODE, s.code); // kod rotuje — trzymaj świeży
        setStoredCode(s.code);
        if (pairStatusRef.current === 'lost') setPairStatus('paired');
      }
      setPairRequests((prev) => prev.filter((r) => (s?.pending || []).some((p) => p.userId === r.userId)));
    };
    const onPairRequest = ({ userId, username }) => {
      setPairRequests((prev) => prev.some((r) => r.userId === userId) ? prev : [...prev, { userId, username }]);
    };
    const onCodeRotated = ({ code, pairToken: pt, qrUrl: q }) => {
      setPairToken(pt);
      setQrUrl(q);
      setSession((s) => (s ? { ...s, code } : s));
    };
    const onLost = () => setPairStatus('lost');
    const onError = (e) => {
      setError(e);
      // Błąd parowania kończy stan 'pending' — inaczej badge kręciłby się w nieskończoność
      // (np. PAIRING_CLOSED gdy TV ma już przypiętą grę).
      setPairStatus((s) => (s === 'pending' ? 'error' : s));
    };
    const onGameBound = ({ gameCode, status }) => setBoundGame({ gameCode, status });
    const onGameUnbound = () => setBoundGame(null);
    const onEnded = () => setBoundGame((g) => (g ? { ...g, status: 'ended' } : g));
    const onLicenseUpdate = ({ license }) => {
      setSession((s) => (s ? { ...s, license } : s));
      if (license?.licensed) setPurchaseStatus(null);
    };
    const onLicenseRequired = ({ license }) => {
      setSession((s) => (s ? { ...s, license } : s));
      setLicenseBlockedAt(Date.now());
    };

    socket.on('tv:registered', onRegistered);
    socket.on('tv:paired', onPaired);
    socket.on('tv:pair_pending', onPending);
    socket.on('tv:pair_rejected', onRejected);
    socket.on('tv:unpaired', onUnpaired);
    socket.on('tv:session_update', onSessionUpdate);
    socket.on('tv:pair_request', onPairRequest);
    socket.on('tv:code_rotated', onCodeRotated);
    socket.on('tv:lost', onLost);
    socket.on('tv:error', onError);
    socket.on('tv:game_bound', onGameBound);
    socket.on('tv:game_unbound', onGameUnbound);
    socket.on('game:ended', onEnded);
    socket.on('tv:license_update', onLicenseUpdate);
    socket.on('tv:license_required', onLicenseRequired);
    return () => {
      for (const [ev, fn] of [
        ['tv:registered', onRegistered], ['tv:paired', onPaired], ['tv:pair_pending', onPending],
        ['tv:pair_rejected', onRejected], ['tv:unpaired', onUnpaired], ['tv:session_update', onSessionUpdate],
        ['tv:pair_request', onPairRequest], ['tv:code_rotated', onCodeRotated], ['tv:lost', onLost],
        ['tv:error', onError], ['tv:game_bound', onGameBound], ['tv:game_unbound', onGameUnbound],
        ['game:ended', onEnded], ['tv:license_update', onLicenseUpdate], ['tv:license_required', onLicenseRequired],
      ]) socket.off(ev, fn);
    };
  }, [socket]);

  // ── TV: routing — boundGame → /tv/game/[code], unbound → /tv ──
  useEffect(() => {
    if (role !== 'tv' || !isTvRoute) return;
    const target = boundGame?.gameCode ? `/tv/game/${boundGame.gameCode}` : '/tv';
    if (pathname !== target && !(boundGame?.gameCode && pathname.startsWith('/tv/game/'))) {
      router.push(target);
    }
  }, [role, isTvRoute, boundGame, pathname, router]);

  // ── TV: re-register po każdym connect (resumeToken) ──
  useEffect(() => {
    if (!socket || !isTvRoute) return;
    const register = () => {
      socket.emit('tv:register', registerPayload());
    };
    if (isConnected) register();
    socket.on('connect', register);
    return () => socket.off('connect', register);
  }, [socket, isTvRoute, isConnected]);

  // ── API ──
  const pairWithCode = useCallback((code, token) => {
    setError(null);
    setPairStatus('pending');
    emitPair(code, token);
  }, [emitPair]);

  // Gość podał nick po sparowaniu (JoinNickModal) — re-pair aktualizuje nazwę
  // kontrolera na TV (serwer rozpoznaje istniejącego kontrolera po userId).
  const refreshPairName = useCallback(() => {
    const code = session?.code || sessionStorage.getItem(SS_CODE);
    if (code && pairStatusRef.current === 'paired') {
      lastPairedUsername.current = `${getDisplayName(userRef.current)}|${userRef.current?._id || ''}`;
      emitPair(code);
    }
  }, [session, emitPair]);

  // Odłączenie telefonu: stan lokalny czyścimy OD RAZU (bez czekania na serwer) — gdy
  // sesja TV wygasła albo gniazdo jest offline, przycisk wcześniej nic nie robił, a zapamiętany
  // kod przy kolejnym połączeniu parował telefon z powrotem.
  const unpair = useCallback(() => {
    const code = session?.code || sessionStorage.getItem(SS_CODE);
    const sessionId = session?.id || sessionStorage.getItem(SS_ID);
    let userId = null;
    try { userId = pairedUserIdRef.current || getOrCreateGuestUserId(userRef.current); } catch { /* SSR */ }
    socket?.emit('tv:unpair', { code, sessionId, userId });
    sessionStorage.removeItem(SS_CODE);
    sessionStorage.removeItem(SS_ID);
    setStoredCode(null);
    setSession(null);
    setPairStatus(null);
    setBoundGame(null);
  }, [socket, session]);

  const acceptPair = useCallback((userId) => {
    setPairRequests((prev) => prev.filter((r) => r.userId !== userId));
    socket?.emit('tv:pair_accept', { code: session?.code, userId });
  }, [socket, session]);

  const rejectPair = useCallback((userId) => {
    setPairRequests((prev) => prev.filter((r) => r.userId !== userId));
    socket?.emit('tv:pair_reject', { code: session?.code, userId });
  }, [socket, session]);

  const kickController = useCallback((userId) => {
    socket?.emit('tv:kick_controller', { code: session?.code, userId });
  }, [socket, session]);

  const openPairing = useCallback(() => {
    socket?.emit('tv:open_pairing', { code: session?.code });
  }, [socket, session]);

  const registerTv = useCallback(() => {
    socket?.emit('tv:register', registerPayload());
  }, [socket]);

  // ── TV: identyfikator instalacji z adresu aplikacji (zostaje po nawigacji wewnątrz SPA) ──
  useEffect(() => {
    if (!isTvRoute) return;
    try {
      const iid = new URLSearchParams(window.location.search).get('iid');
      if (iid && /^[a-f0-9]{64}$/.test(iid)) localStorage.setItem(LS_IID, iid);
    } catch { /* prywatne okno / brak storage */ }
  }, [isTvRoute]);

  // ── TV: most zakupu — aplikacja podaje token Google Play, serwer go weryfikuje ──
  useEffect(() => {
    if (!isTvRoute) return;
    const verify = async (detail) => {
      if (!detail?.purchaseToken) return;
      let installationId = null;
      try { installationId = localStorage.getItem(LS_IID); } catch { /* brak storage */ }
      setPurchaseStatus('verifying');
      try {
        const res = await fetch(`${process.env.NEXT_PUBLIC_SOCKET_URL}/api/tv/license/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ installationId, purchaseToken: detail.purchaseToken, productId: detail.productId }),
        });
        const data = await res.json().catch(() => ({}));
        if (data?.ok) {
          setSession((s) => (s ? { ...s, license: data.license } : s));
          setPurchaseStatus(null);
        } else {
          setPurchaseStatus(data?.error === 'PENDING' ? 'pending' : 'verify_failed');
        }
      } catch {
        setPurchaseStatus('verify_failed');
      }
    };
    const onPurchase = (e) => verify(e.detail);
    const onStatus = (e) => setPurchaseStatus(e.detail || null);
    window.addEventListener('kronikiTvPurchase', onPurchase);
    window.addEventListener('kronikiTvPurchaseStatus', onStatus);
    // Aplikacja mogła podać token zanim strona się zamontowała (przywrócenie przy starcie)
    if (window.__kronikiTvPurchase) verify(window.__kronikiTvPurchase);
    return () => {
      window.removeEventListener('kronikiTvPurchase', onPurchase);
      window.removeEventListener('kronikiTvPurchaseStatus', onStatus);
    };
  }, [isTvRoute]);

  // Prośba do aplikacji TV (adres przechwytywany natywnie — bez mostu JS do strony)
  const purchaseTv = useCallback(() => {
    setPurchaseStatus(null);
    window.location.href = 'kroniki-tv://purchase';
  }, []);
  const restorePurchase = useCallback(() => {
    window.location.href = 'kroniki-tv://restore';
  }, []);

  // Stała referencja — TvLicenseToast ma w zależnościach timer auto-zamknięcia
  const clearLicenseBlocked = useCallback(() => setLicenseBlockedAt(null), []);

  const disconnectAll = useCallback(() => {
    socket?.emit('tv:disconnect_all', { code: session?.code });
  }, [socket, session]);

  const releaseGame = useCallback(() => {
    socket?.emit('tv:release_game', { code: session?.code });
  }, [socket, session]);

  // Pilot: ręka ujawniona na TV (tylko gra 1-osobowa — serwer pilnuje reguły)
  const setShowHand = useCallback((enabled) => {
    socket?.emit('tv:show_hand', { code: session?.code, enabled: !!enabled });
  }, [socket, session]);

  const value = {
    role, session,
    code: session?.code || storedCode,
    isOwner, isPaired: pairStatus === 'paired', pairStatus, error,
    resumeToken, qrUrl, pairToken, pairRequests, boundGame,
    myUserId, remotePrefs, setRemotePref,
    pairWithCode, refreshPairName, unpair, acceptPair, rejectPair, kickController, openPairing,
    registerTv, disconnectAll, releaseGame, setShowHand,
    license: session?.license || null, purchaseStatus, licenseBlockedAt, purchaseTv, restorePurchase,
    clearLicenseBlocked,
    clearError: () => setError(null),
  };

  return <TvContext.Provider value={value}>{children}</TvContext.Provider>;
}

export const useTv = () => {
  const ctx = useContext(TvContext);
  if (!ctx) throw new Error('useTv must be used within TvProvider');
  return ctx;
};
